import { computed, ref, unref, type ComputedRef, type Ref } from 'vue';
import { copyToClipboard } from '~/lib/clipboard';
import { buildClientContactDeletionPatch } from '~/lib/clientContacts';
import { clientPortalRoleLabel } from '~/lib/clientPortalPermissions';
import { formatMoney } from '~/lib/formatters';
import { getRequiredAuthHeaders } from '~/lib/apiAuth';
import type { Client, ClientContact, ClientPortalAccessRecord, Invoice, Project, Quote } from '~/types/models';
import type { ClientPortalContactRow } from '@/components/clients/people/types';
import { toast } from 'vue-sonner';

type MaybeRef<T> = Ref<T> | ComputedRef<T>;

interface Options {
    client: MaybeRef<Client | null>;
    relatedProjects: MaybeRef<Project[]>;
    outstandingInvoices: MaybeRef<Invoice[]>;
    sentClientQuotes: MaybeRef<Quote[]>;
    clientCurrency: MaybeRef<string>;
    clientPortalShareLink: MaybeRef<string>;
    canMutate: MaybeRef<boolean>;
    tenantStore: any;
    clientStore: any;
    isContactDialogOpen: Ref<boolean>;
    editingContact: Ref<ClientContact | null>;
    refreshResolvedClient: () => Promise<void>;
    refreshClientWorkspace: () => Promise<void>;
    normalizeText: (value: unknown) => string;
}

export function useClientWorkspacePeople(options: Options) {
    const portalAccessRecords = ref<ClientPortalAccessRecord[]>([]);
    const portalAccessLoading = ref(false);
    const portalAccessError = ref('');
    const portalInviteLoading = ref(false);
    const portalActionKey = ref('');
    const contactDeleting = ref(false);

    const clientContacts = computed<ClientContact[]>(() => {
        const client = unref(options.client);
        const contacts = Array.isArray(client?.contacts) && client.contacts.length ? client.contacts : [];
        if (contacts.length > 0) {
            return [...contacts].sort((a, b) => Number(b.isPrimary === true) - Number(a.isPrimary === true));
        }

        const fallbackName = options.normalizeText(client?.contactName)
            || options.normalizeText(client?.name)
            || options.normalizeText(client?.email)
            || 'Primary contact';
        const fallbackEmail = options.normalizeText(client?.email);
        const fallbackPhone = options.normalizeText(client?.contactNumber) || options.normalizeText(client?.phone);
        if (!fallbackName && !fallbackEmail && !fallbackPhone) return [];

        return [{
            id: options.normalizeText(client?.contacts?.find((contact) => contact.isPrimary)?.id) || `client-${client?.id || 'contact'}-primary`,
            name: fallbackName || 'Primary contact',
            email: fallbackEmail || '',
            ...(fallbackPhone ? { phone: fallbackPhone } : {}),
            isPrimary: true,
            projectAccessMode: 'all',
            portalRole: 'client_owner',
        }];
    });
    const primaryClientContact = computed(() => clientContacts.value.find((contact) => contact.isPrimary) || clientContacts.value[0] || null);
    const hasPortalInviteableContact = computed(() => clientContacts.value.some((contact) => Boolean(options.normalizeText(contact.email))));
    const acceptedPortalAccessCount = computed(() => portalAccessRecords.value.filter((record) => record.status === 'accepted').length);
    const pendingPortalAccessCount = computed(() => portalAccessRecords.value.filter((record) => record.status === 'pending').length);

    const portalAccessByContact = computed(() => {
        const map = new Map<string, ClientPortalAccessRecord>();
        const rank = { accepted: 0, pending: 1, revoked: 2, expired: 3 } as const;
        for (const record of portalAccessRecords.value) {
            const email = String(record.email || '').trim().toLowerCase();
            const contactId = String(record.contactId || '').trim();
            const key = contactId || email;
            if (!key) continue;
            const existing = map.get(key);
            if (!existing || rank[record.status] < rank[existing.status]) {
                map.set(key, record);
                if (email) map.set(email, record);
                if (contactId) map.set(contactId, record);
            }
        }
        return map;
    });

    const portalContactRows = computed<ClientPortalContactRow[]>(() => {
        const knownEmails = new Set<string>();
        const knownContactIds = new Set<string>();
        const rows = clientContacts.value.map((contact) => {
            const email = String(contact.email || '').trim().toLowerCase();
            if (email) knownEmails.add(email);
            if (contact.id) knownContactIds.add(contact.id);
            return {
                contact,
                access: portalAccessByContact.value.get(contact.id) || (email ? portalAccessByContact.value.get(email) || null : null),
                isLegacy: false,
            };
        });
        const legacyRows = portalAccessRecords.value
            .filter((record) => {
                const email = String(record.email || '').trim().toLowerCase();
                const contactId = String(record.contactId || '').trim();
                return (email || contactId) && !knownEmails.has(email) && !knownContactIds.has(contactId);
            })
            .map((record) => ({
                contact: {
                    id: record.contactId || `legacy-${record.invitationId}`,
                    name: record.contactName || record.userDisplayName || record.email,
                    email: record.email,
                    phone: '',
                    isPrimary: false,
                } as ClientContact,
                access: record,
                isLegacy: true,
            }));
        return [...rows, ...legacyRows];
    });

    const clientDigestRecipientEmail = computed(() => options.normalizeText(primaryClientContact.value?.email) || options.normalizeText(unref(options.client)?.email));
    const clientDigestRecipientName = computed(() => (
        options.normalizeText(primaryClientContact.value?.name)
        || options.normalizeText(unref(options.client)?.contactName)
        || options.normalizeText(unref(options.client)?.name)
        || 'Client'
    ));
    const clientDigestHighlights = computed(() => {
        const outstandingTotal = unref(options.outstandingInvoices).reduce((sum, invoice) => sum + Number(invoice.amountDue || 0), 0);
        return [
            unref(options.relatedProjects).length > 0
                ? `${unref(options.relatedProjects).length} project${unref(options.relatedProjects).length === 1 ? '' : 's'} currently visible from your workspace.`
                : 'No active projects are currently linked to this client.',
            unref(options.sentClientQuotes).length > 0
                ? `${unref(options.sentClientQuotes).length} quote${unref(options.sentClientQuotes).length === 1 ? '' : 's'} ready for review.`
                : 'No quote actions are currently waiting.',
            unref(options.outstandingInvoices).length > 0
                ? `Outstanding invoice balance is ${formatMoney(outstandingTotal, unref(options.outstandingInvoices)[0]?.currency || unref(options.clientCurrency))}.`
                : 'No outstanding invoice balance right now.',
        ];
    });

    function initialLetters(value: string | null | undefined): string {
        const text = String(value || '').trim();
        if (!text) return 'NA';
        return text.split(/\s+/).slice(0, 2).map((part) => part.charAt(0).toUpperCase()).join('');
    }

    function contactProjectAccessSummary(contact: ClientContact, access?: ClientPortalAccessRecord | null): string {
        if (contact.isPrimary || access?.projectAccessMode === 'all' || contact.projectAccessMode === 'all' || access?.portalRole === 'client_owner' || contact.portalRole === 'client_owner') return 'Full client portal access';
        const projectIds = Array.from(new Set([
            ...(Array.isArray(contact.projectIds) ? contact.projectIds : []),
            ...(Array.isArray(access?.projectIds) ? access.projectIds : []),
        ].map((projectId) => String(projectId || '').trim()).filter(Boolean)));
        return projectIds.length === 0 ? 'No project-specific portal access yet' : `${projectIds.length} shared project${projectIds.length === 1 ? '' : 's'}`;
    }

    function contactProjectAccessNames(contact: ClientContact, access?: ClientPortalAccessRecord | null): string {
        if (contact.isPrimary || access?.projectAccessMode === 'all' || contact.projectAccessMode === 'all' || access?.portalRole === 'client_owner' || contact.portalRole === 'client_owner') return 'This contact can see every project shared to this client.';
        const projectIds = Array.from(new Set([
            ...(Array.isArray(contact.projectIds) ? contact.projectIds : []),
            ...(Array.isArray(access?.projectIds) ? access.projectIds : []),
        ].map((projectId) => String(projectId || '').trim()).filter(Boolean)));
        if (projectIds.length === 0) return 'Use a project share flow to grant this contact access to specific projects.';
        return projectIds.map((projectId) => unref(options.relatedProjects).find((project) => project.id === projectId)?.name || projectId).join(', ');
    }

    function contactPortalRoleSummary(contact: ClientContact, access?: ClientPortalAccessRecord | null): string {
        return clientPortalRoleLabel(access?.portalRole || contact.portalRole, contact.isPrimary === true);
    }

    function openAddContactDialog() {
        options.editingContact.value = null;
        options.isContactDialogOpen.value = true;
    }

    function openEditContactDialog(contact: ClientContact) {
        options.editingContact.value = { ...contact };
        options.isContactDialogOpen.value = true;
    }

    function mergeSavedContact(savedContact: ClientContact): ClientContact[] {
        const savedEmail = String(savedContact.email || '').trim().toLowerCase();
        const savedId = String(savedContact.id || '').trim();
        const existingContacts = clientContacts.value.map((contact) => ({ ...contact }));
        const existingIndex = existingContacts.findIndex((contact) => (savedId && contact.id === savedId) || (savedEmail && String(contact.email || '').trim().toLowerCase() === savedEmail));
        if (existingIndex >= 0) {
            existingContacts[existingIndex] = { ...existingContacts[existingIndex], ...savedContact, isPrimary: existingContacts[existingIndex]?.isPrimary === true || savedContact.isPrimary === true };
        } else {
            existingContacts.push(savedContact);
        }
        const primaryIndex = existingContacts.findIndex((contact) => contact.isPrimary === true);
        if (primaryIndex < 0) return existingContacts;
        return existingContacts.map((contact, index) => index === primaryIndex
            ? { ...contact, isPrimary: true, projectAccessMode: 'all', projectIds: [] }
            : { ...contact, isPrimary: false });
    }

    async function refreshPortalAccess(refreshOptions?: { sync?: boolean; showLoading?: boolean }) {
        const client = unref(options.client);
        if (!client?.id) return;
        const shouldSync = refreshOptions?.sync !== false && unref(options.canMutate);
        const showLoading = refreshOptions?.showLoading !== false;
        if (showLoading) portalAccessLoading.value = true;
        portalAccessError.value = '';
        try {
            if (shouldSync) await options.clientStore.syncClientPortalScopes({ clientId: client.id });
            portalAccessRecords.value = await options.clientStore.listClientPortalAccess({ clientId: client.id });
        } catch (error: any) {
            portalAccessError.value = error?.message || 'Failed to load portal access records.';
            portalAccessRecords.value = [];
        } finally {
            if (showLoading) portalAccessLoading.value = false;
        }
    }

    async function handleSaveContact(savedContact: ClientContact) {
        const client = unref(options.client);
        if (!client) return;
        const nextContacts = mergeSavedContact(savedContact);
        const primaryContact = nextContacts.find((contact) => contact.isPrimary) || nextContacts[0] || null;
        await options.clientStore.updateClient(client.id, {
            ...(savedContact.isPrimary && primaryContact ? { contactName: primaryContact.name, email: primaryContact.email, phone: primaryContact.phone || '', contactNumber: primaryContact.phone || '' } : {}),
            contacts: nextContacts,
        });
        await Promise.all([options.refreshResolvedClient(), options.refreshClientWorkspace(), refreshPortalAccess()]);
        options.isContactDialogOpen.value = false;
        options.editingContact.value = null;
        toast.success('Client contact updated.');
    }

    async function handleDeleteContact(contact: ClientContact) {
        const client = unref(options.client);
        if (!client?.id || !unref(options.canMutate)) return;

        contactDeleting.value = true;
        try {
            const contactEmail = String(contact.email || '').trim().toLowerCase();
            const currentAccessRecords = await options.clientStore.listClientPortalAccess({ clientId: client.id });
            const accessRecords = currentAccessRecords.filter((record: ClientPortalAccessRecord) => (
                ['pending', 'accepted'].includes(record.status)
                && (
                    String(record.contactId || '').trim() === contact.id
                    || (contactEmail && String(record.email || '').trim().toLowerCase() === contactEmail)
                )
            ));

            for (const record of accessRecords) {
                await options.clientStore.revokeClientPortalAccess({ clientId: client.id }, record.invitationId);
            }

            const patch = buildClientContactDeletionPatch(client, contact.id);
            if (!patch) {
                throw new Error('This contact is no longer attached to the client.');
            }

            await options.clientStore.updateClient(client.id, patch);
            await Promise.all([
                options.refreshResolvedClient(),
                options.refreshClientWorkspace(),
                refreshPortalAccess({ sync: false }),
            ]);
            options.isContactDialogOpen.value = false;
            options.editingContact.value = null;
            toast.success(`${contact.name || 'Client contact'} deleted.`);
        } catch (error: any) {
            toast.error(error?.message || 'Could not delete the client contact.');
        } finally {
            contactDeleting.value = false;
        }
    }

    async function handleInviteToPortal() {
        const client = unref(options.client);
        const primaryContact = clientContacts.value.find((contact) => contact.isPrimary) || clientContacts.value[0];
        if (!client?.id || !primaryContact) return;
        const primaryContactAccess = portalAccessByContact.value.get(primaryContact.id) || (primaryContact.email ? portalAccessByContact.value.get(String(primaryContact.email).trim().toLowerCase()) || null : null);
        portalInviteLoading.value = true;
        const toastId = toast.loading(`Sending portal invitation to ${primaryContact.email}...`);
        try {
            await options.clientStore.inviteClientContactToPortal({ clientId: client.id }, { contactId: primaryContact.id, contactEmail: primaryContact.email, contactName: primaryContact.name, portalRole: primaryContactAccess?.portalRole || primaryContact.portalRole });
            await refreshPortalAccess({ sync: false, showLoading: false });
            toast.success(`Portal invitation sent to ${primaryContact.email}.`, { id: toastId });
        } catch (error: any) {
            toast.error(error?.message || 'Failed to send portal invitation.', { id: toastId });
        } finally {
            portalInviteLoading.value = false;
        }
    }

    async function handleProvisionPortalAccess() {
        const client = unref(options.client);
        const primaryContact = clientContacts.value.find((contact) => contact.isPrimary) || clientContacts.value[0];
        if (!client?.id || !primaryContact?.email) return;
        portalInviteLoading.value = true;
        const toastId = toast.loading(`Granting portal access to ${primaryContact.email}...`);
        try {
            await options.clientStore.provisionClientContactPortalAccess({ clientId: client.id }, {
                contactId: primaryContact.id,
                contactEmail: primaryContact.email,
                contactName: primaryContact.name,
            });
            await refreshPortalAccess({ sync: false, showLoading: false });
            toast.success(`Portal access granted and password setup sent to ${primaryContact.email}.`, { id: toastId });
        } catch (error: any) {
            toast.error(error?.message || 'Failed to grant portal access.', { id: toastId });
        } finally {
            portalInviteLoading.value = false;
        }
    }

    async function handleInviteContact(contact: ClientContact, access?: ClientPortalAccessRecord | null) {
        const client = unref(options.client);
        if (!client?.id || !contact.email) return;
        const actionKey = `invite:${contact.id}`;
        portalActionKey.value = actionKey;
        const toastId = toast.loading(`Sending portal access email to ${contact.email}...`);
        try {
            await options.clientStore.inviteClientContactToPortal({ clientId: client.id }, { contactId: contact.id, contactEmail: contact.email, contactName: contact.name, portalRole: access?.portalRole || contact.portalRole });
            await refreshPortalAccess({ sync: false, showLoading: false });
            toast.success(`Portal access email sent to ${contact.email}.`, { id: toastId });
        } catch (error: any) {
            toast.error(error?.message || 'Failed to send portal access email.', { id: toastId });
        } finally {
            portalActionKey.value = '';
        }
    }

    async function handleProvisionContact(contact: ClientContact) {
        const client = unref(options.client);
        if (!client?.id || !contact.email) return;
        const actionKey = `provision:${contact.id}`;
        portalActionKey.value = actionKey;
        const toastId = toast.loading(`Granting portal access to ${contact.email}...`);
        try {
            await options.clientStore.provisionClientContactPortalAccess({ clientId: client.id }, {
                contactId: contact.id,
                contactEmail: contact.email,
                contactName: contact.name,
            });
            await refreshPortalAccess({ sync: false, showLoading: false });
            toast.success(`Portal access granted and password setup sent to ${contact.email}.`, { id: toastId });
        } catch (error: any) {
            toast.error(error?.message || 'Failed to grant portal access.', { id: toastId });
        } finally {
            portalActionKey.value = '';
        }
    }

    async function handleResetContactPassword(contact: ClientContact) {
        const client = unref(options.client);
        if (!client?.id || !contact.email) return;
        const actionKey = `reset:${contact.id}`;
        portalActionKey.value = actionKey;
        const toastId = toast.loading(`Sending password reset to ${contact.email}...`);
        try {
            await options.clientStore.sendClientContactPasswordReset({ clientId: client.id }, {
                contactId: contact.id,
                contactEmail: contact.email,
                contactName: contact.name,
            });
            toast.success(`Password reset sent to ${contact.email}.`, { id: toastId });
        } catch (error: any) {
            toast.error(error?.data?.statusMessage || error?.statusMessage || error?.message || 'Failed to send password reset.', { id: toastId });
        } finally {
            portalActionKey.value = '';
        }
    }

    async function handleRevokePortalAccess(record: ClientPortalAccessRecord) {
        const client = unref(options.client);
        if (!client?.id) return;
        portalActionKey.value = `revoke:${record.invitationId}`;
        const toastId = toast.loading(`Revoking portal access for ${record.email}...`);
        try {
            await options.clientStore.revokeClientPortalAccess({ clientId: client.id }, record.invitationId);
            await refreshPortalAccess({ sync: false, showLoading: false });
            toast.success(`Portal access revoked for ${record.email}.`, { id: toastId });
        } catch (error: any) {
            toast.error(error?.message || 'Failed to revoke portal access.', { id: toastId });
        } finally {
            portalActionKey.value = '';
        }
    }

    async function handleViewAsClientPortal() {
        const client = unref(options.client);
        if (!client?.slug || !options.tenantStore.currentTenantId) return;
        await navigateTo(`/portal/${encodeURIComponent(client.slug)}?asClientId=${encodeURIComponent(client.id)}&tenantId=${encodeURIComponent(options.tenantStore.currentTenantId)}`);
    }

    async function handleCopyClientPortalLink() {
        const link = unref(options.clientPortalShareLink);
        if (!link) {
            toast.error('This client needs a portal slug before we can copy a link.');
            return;
        }
        try {
            await copyToClipboard(link);
            toast.success('Client portal link copied.');
        } catch (error: any) {
            toast.error(error?.message || 'Could not copy the client portal link.');
        }
    }

    async function handleSendClientDigest() {
        const client = unref(options.client);
        const link = unref(options.clientPortalShareLink);
        if (!client?.id) return;
        if (!clientDigestRecipientEmail.value) {
            toast.error('Add a client email address before sending a digest.');
            return;
        }
        if (!link) {
            toast.error('Client portal link is not available yet.');
            return;
        }
        const toastId = toast.loading('Sending digest...');
        try {
            await $fetch('/api/send-email', {
                method: 'POST',
                headers: await getRequiredAuthHeaders(),
                body: {
                    tenantId: options.tenantStore.currentTenantId,
                    to: clientDigestRecipientEmail.value,
                    template: 'client-portal-digest',
                    recipientName: clientDigestRecipientName.value,
                    actionLink: link,
                    highlights: clientDigestHighlights.value,
                    clientName: client.name,
                },
            });
            toast.success('Digest sent.', { id: toastId });
        } catch (error: any) {
            toast.error(error?.message || 'Could not send the digest.', { id: toastId });
        }
    }

    return {
        portalAccessRecords,
        portalAccessLoading,
        portalAccessError,
        portalInviteLoading,
        portalActionKey,
        contactDeleting,
        clientContacts,
        primaryClientContact,
        hasPortalInviteableContact,
        acceptedPortalAccessCount,
        pendingPortalAccessCount,
        portalAccessByContact,
        portalContactRows,
        clientDigestRecipientEmail,
        clientDigestRecipientName,
        clientDigestHighlights,
        initialLetters,
        contactProjectAccessSummary,
        contactProjectAccessNames,
        contactPortalRoleSummary,
        openAddContactDialog,
        openEditContactDialog,
        handleSaveContact,
        handleDeleteContact,
        refreshPortalAccess,
        handleInviteToPortal,
        handleProvisionPortalAccess,
        handleInviteContact,
        handleProvisionContact,
        handleResetContactPassword,
        handleRevokePortalAccess,
        handleViewAsClientPortal,
        handleCopyClientPortalLink,
        handleSendClientDigest,
    };
}
